import { api } from "@/shared/api";
import type { AugmentTask } from "@/shared/api/augment";

// FSD entities/product-material — 产品物料(packs W3)。
// 契约锚点:gscq-api ProductMaterialController(base /api/v1/products/{nodeCode}/materials,W1 地基)。
// 读=租户三角色;写=TENANT_MANAGER 收紧(ADMIN 层次继承;产品物料是对客资产,不给 STAFF 开写)。
// 产品**节点**的建/改/删仍平台代客,权限口径不动。

/** 产品物料槽(后端枚举定死;使用场景图不设槽 —— 场景文字进提示词链,设计 §3.1 口径)。 */
export type ProductSlot = "main" | "angle" | "detail" | "package" | "scale";

export const PRODUCT_SLOTS: ProductSlot[] = ["main", "angle", "detail", "package", "scale"];

/** 槽中文名 + 一句话说明(对齐设计 §3.1 消费方矩阵)。 */
export const PRODUCT_SLOT_META: Record<ProductSlot, { label: string; hint: string }> = {
  main: { label: "主图", hint: "正面主图(白底优先,必传)—— 视频产品特写首帧、海报主体、产品树挂图" },
  angle: { label: "多角度", hint: "45°/侧/背/顶 —— 开箱首体验·产品变形·详情长图" },
  detail: { label: "细节", hint: "材质/接口/按钮/标签成分特写 —— 详情长图·视频插播画面" },
  package: { label: "包装", hint: "外盒/开箱平铺(配件全家福)—— 开箱首体验" },
  scale: { label: "尺寸参照", hint: "与手/桌面同框 —— 详情长图·信任建立" },
};

/**
 * 产品物料视图(与人物物料同表同 DTO 口径):`objectKey` 下发(消费端锚 ref 落库永远是 key);
 * `url` = 现签预览(会过期,仅展示这一次);`brandProtect` = 红线标记(logo/文字区禁 AI 重绘,
 * i2i 链护品牌资产 —— 呼应「引擎会把文案画进图」教训)。
 */
export interface ProductMaterialDto {
  id: number;
  slot: ProductSlot;
  label?: string | null;
  objectKey: string;
  source?: string | null;
  sortWeight?: number | null;
  brandProtect?: boolean;
  url?: string | null;
  /** 1280 长边缩略图现签。存量物料回填前为 null —— 消费面必须 `thumbUrl ?? url` 回退。 */
  thumbUrl?: string | null;
}

const base = (nodeCode: string) => `/products/${encodeURIComponent(nodeCode)}/materials`;

export const productMaterialService = {
  list: (nodeCode: string) => api.get<ProductMaterialDto[]>(base(nodeCode)),

  /** 传物料(multipart file+slot+可选 label/brandProtect)。 */
  upload: (
    nodeCode: string,
    file: File,
    slot: ProductSlot,
    opts?: { label?: string; brandProtect?: boolean }
  ): Promise<ProductMaterialDto> => {
    const form = new FormData();
    form.append("file", file);
    form.append("slot", slot);
    if (opts?.label) form.append("label", opts.label);
    if (opts?.brandProtect) form.append("brandProtect", "true");
    return api.post<ProductMaterialDto>(base(nodeCode), form);
  },

  /** 改物料(slot 迁槽 / label / brandProtect;undefined=不动)。 */
  update: (
    nodeCode: string,
    materialId: number,
    patch: { slot?: ProductSlot; label?: string; brandProtect?: boolean }
  ) => api.patch<ProductMaterialDto>(`${base(nodeCode)}/${materialId}`, patch),

  remove: (nodeCode: string, materialId: number) =>
    api.delete<void>(`${base(nodeCode)}/${materialId}`),

  /** slot 内重排:orderedIds 必须恰好覆盖该 slot 全部物料(后端全覆盖校验)。 */
  reorder: (nodeCode: string, slot: ProductSlot, orderedIds: number[]) =>
    api.put<void>(`${base(nodeCode)}/order`, { slot, orderedIds }),

  // ── W5 AI 补足(写口 MANAGER;底图=anchorMaterialId,缺省 main 槽第一张;品牌保护物料后端拒)──

  /** 起单:whiten(白底标准化 ×3)/ angles(多角度 ×4)。anchorMaterialId 须属本产品,否则后端 404。 */
  augment: (nodeCode: string, req: { mode: string; anchorMaterialId?: number }) =>
    api.post<AugmentTask>(`${base(nodeCode)}/augment`, req),

  augmentTask: (nodeCode: string, taskId: number) =>
    api.get<AugmentTask>(`${base(nodeCode)}/augment-tasks/${taskId}`),

  /** 最近 10 条补足任务(新→旧,不带产物);同人物侧,补「面板一关就找不回」那个洞。 */
  augmentTasks: (nodeCode: string) => api.get<AugmentTask[]>(`${base(nodeCode)}/augment-tasks`),

  curateFromTask: (
    nodeCode: string,
    taskId: number,
    picks: { assetId: number; slot: string; label?: string | null }[]
  ) => api.post<ProductMaterialDto[]>(`${base(nodeCode)}/from-task`, { taskId, picks }),
};
